import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { heavy, colors, bold as boldInter, buttonColors, buttonSize } from '@/styles';

/**
 * Base Button
 *
 * @param {import('./Button').ButtonProps} props
 */
function Button({
  className,
  children,
  color,
  disabled,
  icon,
  iconPosition,
  label,
  onClick,
  outline,
  rectangle,
  size,
  type,
  textColor,
  borderColor,
  hoverColor,
  isRebrand,
  ...aria
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${className} ${label ? '' : 'no-label'} icon--${iconPosition}`}
      {...aria}
    >
      {children}
      {iconPosition === 'before' && icon}
      {label && <span className="text">{label}</span>}
      {iconPosition === 'after' && icon}
    </button>
  );
}

Button.propTypes = {
  /** Any style overrides */
  className: PropTypes.string,
  /** The color of the button */
  color: PropTypes.string,
  /** Children to be rendered inside the button */
  children: PropTypes.node,
  /** Color of the text inside the button */
  textColor: PropTypes.string,
  /** Is the button disabled */
  disabled: PropTypes.bool,
  /** Optional icon to add to button */
  icon: PropTypes.node,
  /** Position of the icon. */
  iconPosition: PropTypes.oneOf(['before', 'after']),
  /** Text to be added in the button */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** What to do when clicked */
  onClick: PropTypes.func,
  /** Should the button be an outline */
  outline: PropTypes.bool,
  /** If the button is a rectangle */
  rectangle: PropTypes.bool,
  /** Size of the button */
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large', 'xlarge']),
  /** The type of button this is */
  type: PropTypes.oneOf(['button', 'submit']),
  /** Color of the button border */
  borderColor: PropTypes.string,
  /** Color of the button when in a hover state */
  hoverColor: PropTypes.string,
  /** Should the button have the rebrand font */
  isRebrand: PropTypes.bool,
};

Button.defaultProps = {
  className: '',
  color: 'boulder',
  children: null,
  textColor: colors.white,
  disabled: false,
  icon: null,
  iconPosition: 'before',
  label: '',
  onClick: null,
  outline: false,
  rectangle: false,
  size: 'medium',
  type: 'button',
  borderColor: 'boulder',
  hoverColor: null,
  isRebrand: false,
};

const styledButton = styled(Button)`
  ${(props) => (props.isRebrand ? boldInter() : heavy())}
  transition: 0.3s;
  border: 0;
  color: ${(props) => props.textColor};
  border-radius: ${(props) => (props.rectangle ? '3px' : '2.5rem')};
  cursor: pointer;

  --margin: 0.3rem;

  &.icon--before > svg {
    margin-right: var(--margin);
  }
  &.icon--after > svg {
    margin-left: var(--margin);
  }

  svg {
    margin-top: -0.125rem;
    vertical-align: middle;
  }

  ${'' /* if there is no label, our SVG does not need a right margin (so it is more centered) */}
  &.no-label svg {
    margin-right: 0;
  }

  ${({ size }) => buttonSize({ size })}
  ${(props) => buttonColors(props)}
`;

export default styledButton;
